import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axios';
import './AccountPage.css';
import logo from '../assets/teesergolf-logo.png';
import Navigation from '../components/Navigation';

function AccountPage() {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState({
    email: '',
    telegramChatId: '',
    notificationsEnabled: true
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Form states
  const [emailForm, setEmailForm] = useState({ newEmail: '', password: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [telegramForm, setTelegramForm] = useState({ telegramChatId: '' });

  // Editing states
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [isEditingTelegram, setIsEditingTelegram] = useState(false);

  // Loading states for individual forms
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isSavingTelegram, setIsSavingTelegram] = useState(false);
  const [isTogglingNotifications, setIsTogglingNotifications] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/auth/profile');
      setProfile(response.data);
      setTelegramForm({ telegramChatId: response.data.telegramChatId || '' });
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const clearMessages = () => {
    setError('');
    setSuccessMessage('');
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    clearMessages();

    if (!emailForm.newEmail.trim()) {
      setError('Please enter a new email address');
      return;
    }
    if (!emailForm.password) {
      setError('Please enter your current password to confirm');
      return;
    }

    setIsSavingEmail(true);
    try {
      await axiosInstance.put('/auth/update-email', {
        newEmail: emailForm.newEmail,
        password: emailForm.password
      });
      setSuccessMessage('Email updated successfully. Please log in again.');
      setIsEditingEmail(false);
      setEmailForm({ newEmail: '', password: '' });
      // Log out after email change for security
      setTimeout(() => {
        logout();
        window.location.href = '/login';
      }, 2000);
    } catch (err) {
      console.error('Error updating email:', err);
      setError(err.response?.data?.message || 'Failed to update email. Please try again.');
    } finally {
      setIsSavingEmail(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    clearMessages();

    if (!passwordForm.currentPassword) {
      setError('Please enter your current password');
      return;
    }
    if (!passwordForm.newPassword) {
      setError('Please enter a new password');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setIsSavingPassword(true);
    try {
      await axiosInstance.put('/auth/update-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      setSuccessMessage('Password updated successfully.');
      setIsEditingPassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      console.error('Error updating password:', err);
      setError(err.response?.data?.message || 'Failed to update password. Please try again.');
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleUpdateTelegram = async (e) => {
    e.preventDefault();
    clearMessages();

    setIsSavingTelegram(true);
    try {
      await axiosInstance.post('/auth/link-telegram', {
        telegramChatId: telegramForm.telegramChatId
      });
      setSuccessMessage('Telegram linked successfully.');
      setIsEditingTelegram(false);
      fetchProfile(); // Refresh profile to show updated telegram ID
    } catch (err) {
      console.error('Error linking telegram:', err);
      setError(err.response?.data?.message || 'Failed to link Telegram. Please try again.');
    } finally {
      setIsSavingTelegram(false);
    }
  };

  const handleToggleNotifications = async () => {
    clearMessages();
    setIsTogglingNotifications(true);
    try {
      const newValue = !profile.notificationsEnabled;
      await axiosInstance.patch('/auth/notifications', {
        notificationsEnabled: newValue
      });
      setProfile(prev => ({ ...prev, notificationsEnabled: newValue }));
      setSuccessMessage(`Notifications ${newValue ? 'enabled' : 'disabled'} successfully.`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error toggling notifications:', err);
      setError(err.response?.data?.message || 'Failed to update notification settings. Please try again.');
    } finally {
      setIsTogglingNotifications(false);
    }
  };

  const cancelEdit = (type) => {
    clearMessages();
    switch (type) {
      case 'email':
        setIsEditingEmail(false);
        setEmailForm({ newEmail: '', password: '' });
        break;
      case 'password':
        setIsEditingPassword(false);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        break;
      case 'telegram':
        setIsEditingTelegram(false);
        setTelegramForm({ telegramChatId: profile.telegramChatId || '' });
        break;
      default:
        break;
    }
  };

  return (
    <div className="account-page">
      <header className="account-header">
        <div className="header-content">
          <div className="header-logo">
            <img src={logo} alt="TeeserGolf" />
          </div>
          <Navigation />
        </div>
      </header>

      <main className="account-main">
        <div className="account-content">
          <h1>Account Settings</h1>
          <p className="account-subtitle">Manage your account information</p>

          {isLoading && (
            <div className="loading-message">Loading your profile...</div>
          )}

          {error && (
            <div className="error-message">{error}</div>
          )}

          {successMessage && (
            <div className="success-message">{successMessage}</div>
          )}

          {!isLoading && (
            <div className="settings-sections">
              {/* Email Section */}
              <div className="settings-section">
                <div className="section-header">
                  <h2>Email Address</h2>
                  {!isEditingEmail && (
                    <button
                      className="edit-button"
                      onClick={() => { clearMessages(); setIsEditingEmail(true); }}
                    >
                      Edit
                    </button>
                  )}
                </div>

                {!isEditingEmail ? (
                  <div className="section-value">{profile.email || user?.email}</div>
                ) : (
                  <form onSubmit={handleUpdateEmail} className="edit-form">
                    <div className="form-group">
                      <label htmlFor="newEmail">New Email</label>
                      <input
                        type="email"
                        id="newEmail"
                        value={emailForm.newEmail}
                        onChange={(e) => setEmailForm({ ...emailForm, newEmail: e.target.value })}
                        placeholder="Enter new email address"
                        disabled={isSavingEmail}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="emailPassword">Current Password</label>
                      <input
                        type="password"
                        id="emailPassword"
                        value={emailForm.password}
                        onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                        placeholder="Confirm with your password"
                        disabled={isSavingEmail}
                      />
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="save-button" disabled={isSavingEmail}>
                        {isSavingEmail ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        className="cancel-button"
                        onClick={() => cancelEdit('email')}
                        disabled={isSavingEmail}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Password Section */}
              <div className="settings-section">
                <div className="section-header">
                  <h2>Password</h2>
                  {!isEditingPassword && (
                    <button
                      className="edit-button"
                      onClick={() => { clearMessages(); setIsEditingPassword(true); }}
                    >
                      Change
                    </button>
                  )}
                </div>

                {!isEditingPassword ? (
                  <div className="section-value">••••••••</div>
                ) : (
                  <form onSubmit={handleUpdatePassword} className="edit-form">
                    <div className="form-group">
                      <label htmlFor="currentPassword">Current Password</label>
                      <input
                        type="password"
                        id="currentPassword"
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                        placeholder="Enter current password"
                        disabled={isSavingPassword}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="newPassword">New Password</label>
                      <input
                        type="password"
                        id="newPassword"
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                        placeholder="Enter new password (min 6 characters)"
                        disabled={isSavingPassword}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="confirmPassword">Confirm New Password</label>
                      <input
                        type="password"
                        id="confirmPassword"
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                        placeholder="Confirm new password"
                        disabled={isSavingPassword}
                      />
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="save-button" disabled={isSavingPassword}>
                        {isSavingPassword ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        className="cancel-button"
                        onClick={() => cancelEdit('password')}
                        disabled={isSavingPassword}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Telegram Section */}
              <div className="settings-section">
                <div className="section-header">
                  <h2>Telegram Notifications</h2>
                  {!isEditingTelegram && (
                    <button
                      className="edit-button"
                      onClick={() => { clearMessages(); setIsEditingTelegram(true); }}
                    >
                      {profile.telegramChatId ? 'Edit' : 'Link'}
                    </button>
                  )}
                </div>

                {!isEditingTelegram ? (
                  <div className="section-value">
                    {profile.telegramChatId ? (
                      <span className="telegram-linked">
                        Linked (Chat ID: {profile.telegramChatId})
                      </span>
                    ) : (
                      <span className="telegram-not-linked">Not linked</span>
                    )}
                  </div>
                ) : (
                  <form onSubmit={handleUpdateTelegram} className="edit-form">
                    <div className="telegram-instructions">
                      <p>To get your Telegram Chat ID:</p>
                      <ol>
                        <li>Open Telegram and search for <strong>@TeeserGolfBot</strong>, or <a href="https://t.me/TeeserGolfBot" target="_blank" rel="noopener noreferrer">click here to open the bot</a></li>
                        <li>Start a conversation and send <strong>/start</strong></li>
                        <li>The bot will reply with your Chat ID</li>
                        <li>Copy and paste it below</li>
                      </ol>
                    </div>
                    <div className="form-group">
                      <label htmlFor="telegramChatId">Telegram Chat ID</label>
                      <input
                        type="text"
                        id="telegramChatId"
                        value={telegramForm.telegramChatId}
                        onChange={(e) => setTelegramForm({ telegramChatId: e.target.value })}
                        placeholder="Enter your Telegram Chat ID"
                        disabled={isSavingTelegram}
                      />
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="save-button" disabled={isSavingTelegram}>
                        {isSavingTelegram ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        className="cancel-button"
                        onClick={() => cancelEdit('telegram')}
                        disabled={isSavingTelegram}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Notifications Toggle Section */}
              <div className="settings-section">
                <div className="section-header">
                  <h2>Notifications</h2>
                </div>
                <div className="notifications-toggle-row">
                  <div className="notifications-info">
                    <span className="notifications-label">Enable Notifications</span>
                    <span className="notifications-description">
                      Receive alerts when tee times matching your criteria become available
                    </span>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={profile.notificationsEnabled}
                      onChange={handleToggleNotifications}
                      disabled={isTogglingNotifications}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default AccountPage;
